import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RawMaterialService } from '../../../../raw-material-inventory/raw-material.service';
import { finalize, Subscription } from 'rxjs';
import { RawMaterialCategory, RawMaterialCategoryItem } from '../../../../raw-material-inventory/raw-material.model';
import { DeleteConfirmationModalComponent } from '../../../../common/components/delete-confirmation-modal/delete-confirmation-modal.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Dealer } from '../../../../dealers/dealers.model';
import { toTitleCase } from '../../../../utils';

@Component({
  selector: 'app-raw-material-categories',
  standalone: false,
  templateUrl: './raw-material-categories.component.html',
  styleUrl: './raw-material-categories.component.scss',
})
export class RawMaterialCategoriesComponent implements OnInit, OnDestroy {
  protected categories: RawMaterialCategoryItem[] = [];
  protected showModal = false;
  protected selectedCategory: RawMaterialCategoryItem | null = null;
  protected categoryForm!: FormGroup;
  protected isCategoryDataLoading: boolean = false;
  protected searchTerm: string = '';

  private rawMaterialSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private rawMaterialService: RawMaterialService,
    private modalService: NgbModal
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.getRawMaterialCategories();
  }

  /**
   * The endpoint returns every category in one unpaginated array, so filtering here
   * is instant and costs no request.
   */
  protected get filteredCategories(): RawMaterialCategoryItem[] {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) return this.categories;

    return this.categories.filter(
      (category: RawMaterialCategoryItem) =>
        category.name.toLowerCase().includes(term) || category.short_code.toLowerCase().includes(term)
    );
  }

  protected clearSearch(): void {
    this.searchTerm = '';
  }

  protected openAddCategoryModal(): void {
    this.selectedCategory = null;
    this.categoryForm.reset();
    this.showModal = true;
  }

  protected editCategory(category: RawMaterialCategoryItem): void {
    this.selectedCategory = { ...category };
    this.categoryForm.patchValue({
      name: toTitleCase(category.name),
      short_code: category.short_code,
    });
    this.showModal = true;
  }

  protected closeModal(): void {
    this.showModal = false;
  }

  protected saveCategory() {
    const formValue = this.categoryForm.value;

    if (this.selectedCategory) {
      this.rawMaterialSubscription = this.rawMaterialService
        .update_category(this.selectedCategory.id, {
          name: formValue.name.toLowerCase(),
          short_code: formValue.short_code.toUpperCase(),
        })
        .subscribe({
          next: (updatedRawMaterialCategory: RawMaterialCategoryItem) => {
            const index = this.categories.findIndex(c => c.id === this.selectedCategory?.id);
            if (index > -1) this.categories[index] = updatedRawMaterialCategory;
          },
        });
    } else {
      const newCategory = {
        name: formValue.name.toLowerCase(),
        short_code: formValue.short_code.toUpperCase(),
      };

      this.rawMaterialSubscription = this.rawMaterialService.post_categories(newCategory).subscribe({
        next: (newCreatedCategory: RawMaterialCategoryItem) => {
          this.categories.push(newCreatedCategory);
        },
      });
    }

    this.closeModal();
  }

  protected confirmDeleteCategory(category: RawMaterialCategoryItem): void {
    const modalRef = this.modalService.open(DeleteConfirmationModalComponent, {
      backdrop: 'static',
      keyboard: false,
    });

    modalRef.componentInstance.title = 'Delete Category';
    modalRef.componentInstance.message = 'Are you sure you want to delete this category?';
    modalRef.componentInstance.warning = 'This action cannot be undone and may affect related materials.';

    modalRef.result.then(() => {
      this.rawMaterialSubscription = this.rawMaterialService.delete_category(category.id).subscribe({
        next: () => {
          this.categories = this.categories.filter(c => c.id !== category.id);
        },
      });
    });
  }

  private getRawMaterialCategories(): void {
    this.isCategoryDataLoading = true;
    this.rawMaterialSubscription = this.rawMaterialService
      .get_categories()
      .pipe(finalize(() => (this.isCategoryDataLoading = false)))
      .subscribe({
        next: (rawMaterialCategoriesResponse: RawMaterialCategoryItem[]) => {
          this.categories = rawMaterialCategoriesResponse;
        },
      });
  }

  private initForm(): void {
    this.categoryForm = this.fb.group({
      name: ['', Validators.required],
      short_code: ['', Validators.required],
    });
  }

  ngOnDestroy(): void {
    this.rawMaterialSubscription?.unsubscribe();
    this.showModal = false;
  }
}
